// ============================================================================
// APP CONFIG - SERVICIO DE CONFIGURACIÓN DINÁMICA (MULTI-TENANT)
// ============================================================================
// La configuración se deriva del dominio actual en index.html (un único
// build para todos los clientes). El branding (nombre + logo) viene del
// backend según el subdominio: ?endpoint=get_public_config
// ============================================================================

function buildDefaultConfig() {
  const isLocalDev = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
  // En local dev se puede apuntar a otro tenant con REACT_APP_DEV_API
  // (p.ej. https://skello.jonathanva.com para ver el gads-panel de Skello).
  const apiBase = isLocalDev
    ? (process.env.REACT_APP_DEV_API || 'https://certi.jonathanva.com')
    : window.location.origin;
  return {
    API_URL: apiBase + '/task/backend/routes/api.php',
    BASE_DOMAIN: apiBase,
    CLIENT_NAME: 'Task Manager',
    LOGO_URL: null,
    ENVIRONMENT: isLocalDev ? 'development' : 'production'
  };
}

class AppConfig {
  constructor() {
    // Usar la configuración global construida en index.html antes de React.
    // Si no existe (tests, etc.), derivarla del dominio igual que allí.
    this.config = window.__APP_CONFIG__ || buildDefaultConfig();
    this.loading = false;
    this.loaded = true;
    this.error = null;
  }

  /**
   * Recargar el branding del cliente (nombre + logo) desde el backend.
   * La parte derivada del dominio (API_URL, etc.) no cambia nunca.
   */
  async load() {
    try {
      this.loading = true;

      // Fetch COMPARTIDO con clientConfigService (window.__PUBLIC_CONFIG_FETCH__):
      // ambos loaders necesitan get_public_config al arrancar; así solo viaja una vez.
      if (!window.__PUBLIC_CONFIG_FETCH__) {
        window.__PUBLIC_CONFIG_FETCH__ = fetch(this.getApiUrl() + '?endpoint=get_public_config', {
          cache: 'no-cache'
        }).then((response) => {
          if (!response.ok) {
            throw new Error(`Error cargando branding: ${response.status} ${response.statusText}`);
          }
          return response.json();
        }).catch((err) => {
          delete window.__PUBLIC_CONFIG_FETCH__;
          throw err;
        });
      }
      const result = await window.__PUBLIC_CONFIG_FETCH__;
      const data = result.data || {};

      if (data.nombre_empresa) this.config.CLIENT_NAME = data.nombre_empresa;
      if (data.logo_url) this.config.LOGO_URL = data.logo_url;
      if (data.task_prefix) this.config.TASK_PREFIX = data.task_prefix;
      if (Array.isArray(data.responsables) && data.responsables.length) {
        this.config.RESPONSABLES = data.responsables;
      }
      window.__APP_CONFIG__ = this.config;
      this.error = null;

      return this.config;

    } catch (error) {
      console.error('❌ Error recargando branding:', error);
      this.error = error.message;
      return this.config; // Mantener la config actual

    } finally {
      this.loading = false;
    }
  }

  /**
   * Obtener la URL de la API
   */
  getApiUrl() {
    if (!this.config || !this.config.API_URL) {
      console.warn('⚠️ Config no disponible, derivando del dominio');
      this.config = buildDefaultConfig();
    }
    return this.config.API_URL;
  }

  /**
   * Obtener el nombre del cliente
   */
  getClientName() {
    return this.config?.CLIENT_NAME || 'Default Client';
  }

  /**
   * Prefijo de IDs de tarea del tenant (CERTI, YOMO...). Null si la
   * migración de numeración aún no se ha ejecutado.
   */
  getTaskPrefix() {
    return this.config?.TASK_PREFIX || null;
  }

  /**
   * Responsables del tenant (clients.responsables). Fallback a la lista
   * clásica si el backend no la devuelve.
   */
  getResponsables() {
    return this.config?.RESPONSABLES || ['Backend', 'Frontend', 'SEO', 'Marketing', 'Content'];
  }

  /**
   * Obtener el entorno
   */
  getEnvironment() {
    return this.config?.ENVIRONMENT || 'development';
  }

  /**
   * Verificar si está en producción
   */
  isProduction() {
    return this.getEnvironment() === 'production';
  }

  /**
   * Verificar si está en desarrollo
   */
  isDevelopment() {
    return this.getEnvironment() === 'development';
  }

  /**
   * Obtener toda la configuración
   */
  getAll() {
    return this.config;
  }

  /**
   * Recargar la configuración
   */
  async reload() {
    console.log('🔄 Recargando configuración...');
    this.config = window.__APP_CONFIG__ || buildDefaultConfig();
    return await this.load();
  }

  /**
   * Verificar si la configuración está lista
   */
  isReady() {
    return this.loaded && this.config !== null;
  }
}

// Exportar una instancia singleton
const appConfig = new AppConfig();

export default appConfig;

// ============================================================================
// HELPER PARA COMPONENTES DE REACT
// ============================================================================
// NOTA: Si necesitas usar la configuración en componentes React,
// simplemente importa appConfig directamente y usa sus métodos:
//
// import appConfig from '../config/appConfig';
//
// function MyComponent() {
//   const apiUrl = appConfig.getApiUrl();
//   const clientName = appConfig.getClientName();
//   // ...
// }
//
// No necesitas un hook personalizado porque la configuración se carga
// una vez al inicio en index.js y está disponible globalmente.

// ============================================================================
// FUNCIÓN DE INICIALIZACIÓN
// ============================================================================

/**
 * Inicializar la configuración al inicio de la aplicación
 * Debe ser llamada antes de cualquier llamada a la API
 */
export const initializeAppConfig = async () => {
  console.log('🚀 Inicializando configuración de la aplicación...');
  try {
    await appConfig.load();
    console.log('✅ Configuración inicializada correctamente');
    return true;
  } catch (error) {
    console.error('❌ Error inicializando configuración:', error);
    return false;
  }
};
